import { useEffect, useMemo, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Label } from "components/input/Label";
import ParametrosFiscaisService from "service/fiscal/ParametrosFiscaisService";

export interface CampoParametro<T> {
    chave: keyof T;
    label: string;
    tipo: "referencia" | "percentual" | "boolean";
    referencia?: string;
}
const service = new ParametrosFiscaisService();

export default function CamposParametrosFiscais<T extends object>({ dados, campos, onChange, vertical = false, larguraCampo = '50%', booleanosObrigatorios = false }: {
    dados: T; campos: CampoParametro<T>[]; onChange: (alteracao: Partial<T>) => void; vertical?: boolean; larguraCampo?: string;
    booleanosObrigatorios?: boolean;
}) {
    const referencias = useMemo(() => [...new Set(campos.map(c => c.referencia).filter((r): r is string => !!r))], [campos]);
    const [opcoes, setOpcoes] = useState<Record<string, { label: string; value: string | number }[]>>({});
    const [erro, setErro] = useState("");
    useEffect(() => {
        let ativo = true;
        setErro("");
        Promise.all(referencias.map(async r => ({ referencia: r, opcoes: await service.opcoes(r) })))
            .then(resultados => { if (ativo) setOpcoes(Object.fromEntries(resultados.map(r => [r.referencia, r.opcoes]))); })
            .catch(() => { if (ativo) setErro("Não foi possível carregar os tratamentos fiscais. Verifique a permissão de consultar parâmetros fiscais."); });
        return () => { ativo = false; };
    }, [referencias]);
    return <div className={vertical ? "" : "grid"}>
        {campos.map(c => {
            const obrigatorio = booleanosObrigatorios && c.tipo === "boolean";
            const input = c.tipo === "percentual"
                ? <InputNumber value={typeof dados[c.chave] === "number" ? dados[c.chave] as number : null} locale="pt-BR" min={0} max={100} maxFractionDigits={6}
                    onValueChange={e => onChange({ [c.chave]: e.value ?? null } as Partial<T>)} className="w-full"
                    style={vertical ? { width: larguraCampo } : undefined} />
                : <Dropdown value={dados[c.chave] ?? null} options={c.tipo === "boolean" ? [{ label: "Sim", value: true }, { label: "Não", value: false }] : opcoes[c.referencia ?? ""] ?? []}
                    optionLabel="label" optionValue="value" filter={c.tipo === "referencia"}
                    required={obrigatorio} aria-required={obrigatorio} showClear={!obrigatorio}
                    placeholder={obrigatorio ? "Selecione Sim ou Não" : "Não configurado / herdar"}
                    onChange={e => onChange({ [c.chave]: e.value } as Partial<T>)} className="w-full"
                    style={vertical ? { width: larguraCampo } : undefined} />;

            return <div key={String(c.chave)} className={vertical ? "row mb-3" : "col-12 md:col-4"}>
                <Label obrigatorio={obrigatorio} className={vertical ? "col col-sm-12 mb-2" : undefined}>{c.label}</Label>
                {vertical ? <div className="col-sm-12">{input}</div> : input}
            </div>;
        })}
        {erro && <small className="p-error col-12">{erro}</small>}
    </div>;
}
